import React, { useEffect, useRef, useState } from 'react';
import { Box, Input, Menu, MenuButton, MenuItem, MenuList, Text, keyframes } from '@chakra-ui/react';
import { ShoppingCartOutlined, ViewSidebarOutlined, CloseFullscreen, MoreVert, Close, ArrowUpward, Stop } from '@mui/icons-material';

import { useAgent } from './AgentProvider';
import { useAiTheme } from './aiTheme';
import { useShopUI } from '../contexts/ShopUIContext';
import { UserBubble, AssistantTurn, Thinking } from './messages';
import Sparkle from './Sparkle';
import useIsPhone from '../hooks/useIsPhone';

// Raano AI panel (design-system.md §9.2): floating 480×720 on desktop (or a full-height side panel when
// expanded), a bottom sheet on phones. Header, scrolling messages, pill input.

const panelIn = keyframes`from { opacity: 0; transform: translateY(16px) scale(.98) } to { opacity: 1; transform: none }`;
const sheetIn = keyframes`from { transform: translateY(100%) } to { transform: none }`;

const AgentPanel = () => {

    const { open, close, expanded, setExpanded, messages, status, sendText, stop, reset, cartDot, setCartDot } = useAgent();
    const { openCart, openQuickAdd } = useShopUI();
    const t = useAiTheme();
    const phone = useIsPhone();
    const [text, setText] = useState('');
    const scroller = useRef();
    const input = useRef();

    useEffect(() => { scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' }); }, [messages, status]);
    useEffect(() => { if (open && !phone) setTimeout(() => input.current?.focus(), 250); }, [open, phone]);
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === 'Escape' && close();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, close]);

    if (!open) return null;

    const submit = (e) => { e.preventDefault(); if (status === 'thinking') { stop(); return; } sendText(text); setText(''); };
    const addFromChat = (product) => openQuickAdd(product);
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant')?.id;

    const frame = phone
        ? { position: 'fixed', left: 0, right: 0, bottom: 0, height: '85vh', borderTopRadius: '24px', animation: `${sheetIn} .25s cubic-bezier(0.33, 1, 0.68, 1)` }
        : expanded
            ? { position: 'fixed', top: '16px', right: '16px', bottom: '16px', width: '520px', borderRadius: '24px', animation: `${panelIn} .2s ease-out` }
            : { position: 'fixed', right: '24px', bottom: '24px', width: '480px', height: 'min(720px, calc(100vh - 48px))', borderRadius: '24px', animation: `${panelIn} .2s ease-out` };

    const headBtn = { width: '40px', height: '40px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.text, _hover: { bg: t.hover } };

    return (
        <>
            {phone && <Box position='fixed' inset={0} bg='blackAlpha.500' zIndex={590} onClick={close} />}
            <Box
                role='dialog' aria-label='Raano AI shopping assistant'
                {...frame} zIndex={600} bg={t.panel} color={t.text} border='1px solid' borderColor={t.border}
                boxShadow='0 12px 48px rgba(0,0,0,0.18)' display='flex' flexDirection='column' overflow='hidden'
                fontFamily='ai' sx={{ fontStretch: '100%' }}
            >
                {phone && <Box mx='auto' mt='8px' width='40px' height='4px' borderRadius='full' bg={t.border} />}

                {/* Header */}
                <Box display='flex' alignItems='center' gap='12px' px='20px' py='12px' flexShrink={0} >
                    <Box width='44px' height='44px' borderRadius='full' bg={t.avatar} color='#fff' display='flex' alignItems='center' justifyContent='center' flexShrink={0} ><Sparkle size={22} /></Box>
                    <Text fontSize='16px' fontWeight={700} lineHeight='22px' flex={1} >RAANO AI</Text>
                    <Box as='button' aria-label='Open cart' position='relative' {...headBtn} onClick={() => { setCartDot(false); openCart(); }} >
                        <ShoppingCartOutlined sx={{ fontSize: 22 }} />
                        {cartDot && <Box position='absolute' top='8px' right='8px' width='8px' height='8px' borderRadius='full' bg='#E53E3E' />}
                    </Box>
                    {!phone && <Box as='button' aria-label={expanded ? 'Collapse panel' : 'Expand to side panel'} {...headBtn} onClick={() => setExpanded(!expanded)} >
                        {expanded ? <CloseFullscreen sx={{ fontSize: 20 }} /> : <ViewSidebarOutlined sx={{ fontSize: 22 }} />}
                    </Box>}
                    <Menu placement='bottom-end' >
                        <MenuButton as={Box} role='button' aria-label='More options' {...headBtn} cursor='pointer' ><Box display='flex' justifyContent='center' ><MoreVert sx={{ fontSize: 22 }} /></Box></MenuButton>
                        <MenuList zIndex={700} fontSize='14px' color='raano.black' >
                            <MenuItem onClick={reset} >New conversation</MenuItem>
                        </MenuList>
                    </Menu>
                    <Box as='button' aria-label='Close' {...headBtn} onClick={close} ><Close sx={{ fontSize: 22 }} /></Box>
                </Box>

                {/* Messages */}
                <Box ref={scroller} flex={1} overflowY='auto' px='20px' pt='8px' pb='16px' display='flex' flexDirection='column' gap='18px' aria-live='polite' >
                    {messages.map(m => m.role === 'user'
                        ? <UserBubble key={m.id} text={m.blocks[0].markdown} t={t} />
                        : <AssistantTurn key={m.id} blocks={m.blocks} t={t} onAdd={addFromChat} last={m.id === lastAssistant && status !== 'thinking'} />)}
                    {status === 'thinking' && <Thinking t={t} />}
                </Box>

                {/* Input */}
                <Box as='form' onSubmit={submit} px='12px' pt='4px' pb={phone ? 'calc(12px + env(safe-area-inset-bottom))' : '14px'} flexShrink={0} >
                    <Box display='flex' alignItems='center' height='48px' pl='20px' pr='4px' borderRadius='full' border='1px solid' borderColor={t.inputBorder} bg={t.input} _focusWithin={{ borderColor: t.text }} >
                        <Input ref={input} variant='unstyled' placeholder='Message Raano AI' aria-label='Message Raano AI' fontSize='16px' color={t.text} _placeholder={{ color: t.muted }} value={text} onChange={(e) => setText(e.target.value)} />
                        <Box as='button' type='submit' aria-label={status === 'thinking' ? 'Stop' : 'Send'} width='40px' height='40px' borderRadius='full' flexShrink={0}
                            bg={status === 'thinking' || text.trim() ? t.cardBtnBg : t.hover} color={status === 'thinking' || text.trim() ? t.cardBtnText : t.muted}
                            display='flex' alignItems='center' justifyContent='center' transition='background .125s' disabled={status !== 'thinking' && !text.trim()} >
                            {status === 'thinking' ? <Stop sx={{ fontSize: 20 }} /> : <ArrowUpward sx={{ fontSize: 20 }} />}
                        </Box>
                    </Box>
                    <Text textAlign='center' fontSize='11px' color={t.muted} mt='6px' >Raano AI can make mistakes. Check sizes and prices before you buy.</Text>
                </Box>
            </Box>
        </>
    );
};

export default AgentPanel;
